/**
 * Search everything.
 *
 * The command palette is the one search in the chrome, so it has to answer the
 * same way wherever it is opened: a policy is findable from chat, a setting
 * from governance, a thread from the inbox. This module turns every searchable
 * thing in the workspace into palette groups — one group per kind, so results
 * stay clearly separated — and leaves matching, ranking and capping to the
 * palette's own filter (`filterCommandPaletteGroups`).
 *
 * Two layers:
 * - `buildGlobalSearchGroups` is pure: data and a `go` function in, groups out.
 *   It is what the unit tests exercise.
 * - `useGlobalSearchGroups` gathers the data. It is only mounted while the
 *   palette is open, so a closed palette costs no requests; each source reports
 *   its own pending state, which the palette renders as a loading row inside
 *   that source's group rather than blocking the whole list.
 *
 * @module components/globalSearch
 */
import { useNavigate } from "@tanstack/react-router";
import {
  BlocksIcon,
  CalendarClockIcon,
  FileCheckIcon,
  FileTextIcon,
  HouseIcon,
  InboxIcon,
  LandmarkIcon,
  LayersIcon,
  LayoutGridIcon,
  LightbulbIcon,
  MessagesSquareIcon,
  PlugZapIcon,
  RadarIcon,
  SearchCheckIcon,
  SettingsIcon,
  ShieldCheckIcon,
  SlidersHorizontalIcon,
  type LucideIcon,
} from "lucide-react";
import { useCallback, useMemo } from "react";

import { useAutomations, type AutomationView } from "../state/automations";
import { useConnections, type ConnectionView } from "../state/connections";
import {
  useFrameworks,
  useGovernanceFrameworks,
  useIntegrations,
  useOrganizationFindings,
  useRecommendations,
  type Framework,
  type GovernanceFramework,
  type Integration,
  type PolicyFinding,
  type Recommendation,
} from "../state/governance";
import { useInbox, type InboxItem } from "../state/inbox";
import { policyDisplayName, usePolicies, type Policy } from "../state/policies";
import { orgUrlSegment } from "../orgRouting";
import {
  ITEM_ICON_CLASS,
  type CommandPaletteActionItem,
  type CommandPaletteGroup,
} from "./CommandPalette.logic";
import { LIBRARY_NAV_ITEMS } from "./regulation/RegulationSidebarNav";
import { inboxItemHeadline } from "./inbox/inboxPresentation";
import { useRegulationOrg } from "./regulation/useRegulationOrg";
import { settingsSectionSlug } from "./settings/settingsPaths";
import {
  SETTINGS_SEARCH_ITEMS,
  SETTINGS_SECTION_LABELS,
  type SettingsPath,
  type SettingsSearchItem,
} from "./settings/settingsSearch";

/** Results shown per kind while searching; the rest is one more keystroke away. */
export const GLOBAL_SEARCH_GROUP_LIMIT = 6;

/** Where a result leads. Kept as data so the builder stays pure and testable. */
export type GlobalSearchDestination =
  | { readonly kind: "route"; readonly to: string; readonly params?: Record<string, string> }
  | { readonly kind: "settings"; readonly path: SettingsPath; readonly targetId?: string };

export interface GlobalSearchSources {
  /** URL segment of the active company; null when none is resolved yet. */
  readonly orgSegment: string | null;
  readonly policies: ReadonlyArray<Policy>;
  readonly findings: ReadonlyArray<PolicyFinding>;
  readonly recommendations: ReadonlyArray<Recommendation>;
  readonly regimes: ReadonlyArray<Framework>;
  readonly frameworks: ReadonlyArray<GovernanceFramework>;
  readonly integrations: ReadonlyArray<Integration>;
  readonly inbox: ReadonlyArray<InboxItem>;
  readonly connections: ReadonlyArray<ConnectionView>;
  readonly automations: ReadonlyArray<AutomationView>;
  /** Group values whose source is still loading. */
  readonly pending: ReadonlySet<string>;
}

const icon = (Icon: LucideIcon) => <Icon aria-hidden className={ITEM_ICON_CLASS} />;

const compact = (values: ReadonlyArray<string | null | undefined>): string[] =>
  values.filter((value): value is string => typeof value === "string" && value.trim().length > 0);

/**
 * Every searchable thing, grouped by kind. `searchTerms` are ordered: the
 * palette ranks a hit in an earlier term above a hit in a later one, so the
 * name always comes first and descriptive text last.
 */
export function buildGlobalSearchGroups(
  sources: GlobalSearchSources,
  go: (destination: GlobalSearchDestination) => Promise<void>,
): CommandPaletteGroup[] {
  const org = sources.orgSegment;
  const action = (input: {
    value: string;
    title: string;
    description?: string | undefined;
    searchTerms: ReadonlyArray<string | null | undefined>;
    icon: LucideIcon;
    destination: GlobalSearchDestination;
  }): CommandPaletteActionItem => ({
    kind: "action",
    value: input.value,
    title: input.title,
    ...(input.description ? { description: input.description } : {}),
    searchTerms: compact([input.title, ...input.searchTerms]),
    icon: icon(input.icon),
    run: () => go(input.destination),
  });
  const route = (to: string, params?: Record<string, string>): GlobalSearchDestination => ({
    kind: "route",
    to,
    ...(params ? { params } : {}),
  });

  const groups: CommandPaletteGroup[] = [];
  const push = (value: string, label: string, items: CommandPaletteActionItem[]) => {
    const loading = sources.pending.has(value);
    if (items.length === 0 && !loading) return;
    groups.push({ value, label, items, limit: GLOBAL_SEARCH_GROUP_LIMIT, loading });
  };

  // Places first: the fastest answer to "take me to…".
  const places: CommandPaletteActionItem[] = [];
  if (org) {
    places.push(
      action({
        value: "goto:home",
        title: "Home",
        searchTerms: ["start landing overview waiting"],
        icon: HouseIcon,
        destination: route("/$org", { org }),
      }),
      action({
        value: "goto:chat",
        title: "Chat",
        searchTerms: ["sessions threads eve ask"],
        icon: MessagesSquareIcon,
        destination: route("/$org/chat", { org }),
      }),
      action({
        value: "goto:inbox",
        title: "Inbox",
        searchTerms: ["notifications updates invitations"],
        icon: InboxIcon,
        destination: route("/$org/inbox", { org }),
      }),
      action({
        value: "goto:spaces",
        title: "Spaces",
        searchTerms: ["projects workspaces matters"],
        icon: LayersIcon,
        destination: route("/$org/spaces", { org }),
      }),
      action({
        value: "goto:apps",
        title: "Apps",
        searchTerms: ["launcher directory all apps"],
        icon: LayoutGridIcon,
        destination: route("/$org/apps", { org }),
      }),
      action({
        value: "goto:plugins",
        title: "Plugins & Skills",
        searchTerms: ["connections integrations apps tools"],
        icon: BlocksIcon,
        destination: route("/$org/connections", { org }),
      }),
      action({
        value: "goto:automations",
        title: "Automations",
        searchTerms: ["schedules recurring tasks"],
        icon: CalendarClockIcon,
        destination: route("/$org/automations", { org }),
      }),
    );
    // Probe opens from the Compliance header, not the sidebar, so it is not
    // among the navigation sections below — but it is a place all the same.
    places.push(
      action({
        value: "goto:probe",
        title: "Probe",
        description: "Rehearse the regulator's interview",
        searchTerms: [
          "rehearsal rehearse mock interview examination examiner regulator voice call practice",
        ],
        icon: RadarIcon,
        destination: route("/$org/apps/$appId", { org, appId: "probe" }),
      }),
    );
    for (const section of LIBRARY_NAV_ITEMS) {
      places.push({
        kind: "action",
        value: `goto:governance:${section.to}`,
        title: section.label,
        description: "Governance",
        searchTerms: compact([section.label, "governance compliance"]),
        icon: <section.icon aria-hidden className={ITEM_ICON_CLASS} />,
        run: () => go(route(section.to, { org })),
      });
    }
  }
  for (const [path, label] of Object.entries(SETTINGS_SECTION_LABELS) as Array<
    [SettingsPath, string]
  >) {
    places.push(
      action({
        value: `goto:settings:${path}`,
        title: label,
        description: "Settings",
        searchTerms: ["settings preferences"],
        icon: SettingsIcon,
        destination: { kind: "settings", path },
      }),
    );
  }
  push("global:places", "Go to", places);

  // Apps open from Home's sidebar; they are not rail destinations.
  if (org) {
    push("global:apps", "Apps", [
      action({
        value: "app:contract-intelligence",
        title: "Contract Intelligence",
        searchTerms: ["contracts documents review check agreements"],
        icon: FileCheckIcon,
        destination: route("/$org/apps/contract-intelligence", { org }),
      }),
    ]);
  }

  if (org) {
    push(
      "global:policies",
      "Policies & procedures",
      sources.policies.map((policy) =>
        action({
          value: `policy:${policy.id}`,
          title: policyDisplayName(policy),
          description:
            policy.status === "ready"
              ? undefined
              : policy.status === "failed"
                ? "Processing failed"
                : "Processing",
          searchTerms: [policy.filename, policy.documentKind, "policy procedure document"],
          icon: FileTextIcon,
          destination: route("/$org/compliance/policies/$policyId", { org, policyId: policy.id }),
        }),
      ),
    );
    push(
      "global:findings",
      "Findings",
      sources.findings.map((finding) =>
        action({
          value: `finding:${finding.id}`,
          title: finding.title,
          description: compact([finding.severity, finding.status]).join(" · "),
          searchTerms: [finding.summary, finding.sourceTitle, finding.status, "finding"],
          icon: SearchCheckIcon,
          destination: route("/$org/compliance/findings/$findingId", {
            org,
            findingId: finding.id,
          }),
        }),
      ),
    );
    push(
      "global:suggestions",
      "Suggestions",
      sources.recommendations.map((recommendation) =>
        action({
          value: `suggestion:${recommendation.id}`,
          title: recommendation.title,
          description: compact([recommendation.severity, recommendation.status]).join(" · "),
          searchTerms: [recommendation.rationale, recommendation.status, "suggestion"],
          icon: LightbulbIcon,
          destination: route("/$org/compliance/suggestions/$recommendationId", {
            org,
            recommendationId: recommendation.id,
          }),
        }),
      ),
    );
    push(
      "global:regimes",
      "Regimes",
      sources.regimes.map((regime) =>
        action({
          value: `regime:${regime.id}`,
          title: regime.name,
          description: compact([regime.issuer, regime.jurisdiction]).join(" · "),
          searchTerms: [regime.slug, regime.issuer, regime.jurisdiction, regime.description],
          icon: LandmarkIcon,
          destination: route("/$org/compliance/regimes/$regimeId", { org, regimeId: regime.id }),
        }),
      ),
    );
    push(
      "global:frameworks",
      "Frameworks",
      sources.frameworks.map((framework) =>
        action({
          value: `framework:${framework.id}`,
          title: framework.name,
          description: compact([framework.domain, framework.description]).join(" · "),
          searchTerms: [framework.slug, framework.domain, framework.description, "framework"],
          icon: ShieldCheckIcon,
          destination: route("/$org/compliance/frameworks/$frameworkId", {
            org,
            frameworkId: framework.id,
          }),
        }),
      ),
    );
    push(
      "global:integrations",
      "Apps",
      sources.integrations.map((integration) =>
        action({
          value: `integration:${integration.id}`,
          title: integration.name,
          description: integration.connected ? "Connected" : undefined,
          searchTerms: [integration.description, integration.source, "integration"],
          icon: PlugZapIcon,
          destination: route("/$org/apps/$appId", {
            org,
            appId: integration.id,
          }),
        }),
      ),
    );
    push(
      "global:inbox",
      "Inbox",
      sources.inbox.map((item) => {
        const text = inboxItemHeadline(item);
        return action({
          value: `inbox:${item.id}`,
          title: text.title,
          description: text.detail,
          searchTerms: [text.detail, "inbox"],
          icon: InboxIcon,
          destination: route("/$org/inbox", { org }),
        });
      }),
    );
    push(
      "global:plugins",
      "Plugins",
      sources.connections.map((connection) =>
        action({
          value: `plugin:${connection.id}`,
          title: connection.name,
          description: connection.connectionStatus === "connected" ? "Connected" : undefined,
          searchTerms: [connection.description, connection.source, "plugin connection"],
          icon: BlocksIcon,
          destination: route("/$org/connections/$pluginId", { org, pluginId: connection.id }),
        }),
      ),
    );
    push(
      "global:automations",
      "Automations",
      sources.automations.map((automation) =>
        action({
          value: `automation:${automation.id}`,
          title: automation.title,
          description: automation.scheduleLabel,
          searchTerms: [automation.prompt, automation.scheduleLabel, "automation"],
          icon: CalendarClockIcon,
          destination: route("/$org/automations", { org }),
        }),
      ),
    );
  }

  push(
    "global:settings",
    "Settings",
    SETTINGS_SEARCH_ITEMS.map((item: SettingsSearchItem) =>
      action({
        value: `setting:${item.id}`,
        title: item.title,
        description: SETTINGS_SECTION_LABELS[item.to],
        searchTerms: [SETTINGS_SECTION_LABELS[item.to], "setting preference"],
        icon: SlidersHorizontalIcon,
        destination: { kind: "settings", path: item.to, targetId: item.targetId ?? item.id },
      }),
    ),
  );

  return groups;
}

/** Turns a destination into a navigation, in the active company. */
function useGlobalSearchNavigation(orgSegment: string | null) {
  const navigate = useNavigate();
  return useCallback(
    async (destination: GlobalSearchDestination) => {
      if (destination.kind === "route") {
        // Destinations are built from the route table's own paths above; the
        // router's literal-path typing cannot follow them through data.
        await navigate({ to: destination.to, params: destination.params } as never);
        return;
      }
      const location = orgSegment
        ? {
            to: "/$org/settings/$section",
            params: { org: orgSegment, section: settingsSectionSlug(destination.path) },
          }
        : { to: destination.path };
      await navigate({
        ...location,
        ...(destination.targetId ? { hash: destination.targetId } : {}),
      } as never);
    },
    [navigate, orgSegment],
  );
}

/** The governance sources and which of them are still on their first load. */
function useGovernanceSources(organizationId: string | null) {
  const policies = usePolicies(organizationId);
  const findings = useOrganizationFindings(organizationId);
  const recommendations = useRecommendations(organizationId);
  const regimes = useFrameworks(organizationId);
  const frameworks = useGovernanceFrameworks(organizationId);
  const integrations = useIntegrations(organizationId);

  const pending = useMemo(() => {
    const set = new Set<string>();
    if (!organizationId) return set;
    const mark = (value: string, isPending: boolean, hasData: boolean) => {
      if (isPending && !hasData) set.add(value);
    };
    mark("global:policies", policies.isPending, policies.snapshot !== null);
    mark("global:findings", findings.isPending, findings.snapshot !== null);
    mark("global:suggestions", recommendations.isPending, recommendations.snapshot !== null);
    mark("global:regimes", regimes.isPending, regimes.snapshot !== null);
    mark("global:frameworks", frameworks.isPending, frameworks.snapshot !== null);
    mark("global:integrations", integrations.isPending, integrations.snapshot !== null);
    return set;
  }, [
    organizationId,
    policies.isPending,
    policies.snapshot,
    findings.isPending,
    findings.snapshot,
    recommendations.isPending,
    recommendations.snapshot,
    regimes.isPending,
    regimes.snapshot,
    frameworks.isPending,
    frameworks.snapshot,
    integrations.isPending,
    integrations.snapshot,
  ]);

  return useMemo(
    () => ({
      policies: policies.snapshot?.policies ?? [],
      findings: findings.snapshot?.findings ?? [],
      recommendations: recommendations.snapshot?.recommendations ?? [],
      regimes: regimes.snapshot?.frameworks ?? [],
      frameworks: frameworks.snapshot?.frameworks ?? [],
      integrations: integrations.snapshot?.integrations ?? [],
      pending,
    }),
    [
      policies.snapshot,
      findings.snapshot,
      recommendations.snapshot,
      regimes.snapshot,
      frameworks.snapshot,
      integrations.snapshot,
      pending,
    ],
  );
}

/**
 * The live groups. Mount only while the palette is open: every source here is
 * a cached, per-company atom, so reopening the palette is instant and a closed
 * one fetches nothing.
 */
export function useGlobalSearchGroups(): ReadonlyArray<CommandPaletteGroup> {
  const { org } = useRegulationOrg();
  const organizationId = org?.id ?? null;
  const orgSegment = organizationId ? orgUrlSegment(organizationId) : null;
  const governance = useGovernanceSources(organizationId);
  const automations = useAutomations(organizationId);
  const inbox = useInbox();
  const connections = useConnections();
  const go = useGlobalSearchNavigation(orgSegment);

  return useMemo(
    () =>
      buildGlobalSearchGroups(
        {
          orgSegment,
          ...governance,
          inbox: inbox.items,
          connections: connections.connections,
          automations: automations.automations,
        },
        go,
      ),
    [orgSegment, governance, inbox.items, connections.connections, automations.automations, go],
  );
}

/** Group values that belong to the governance section. */
const GOVERNANCE_GROUP_VALUES: ReadonlySet<string> = new Set([
  "global:policies",
  "global:findings",
  "global:suggestions",
  "global:regimes",
  "global:frameworks",
  "global:integrations",
]);

/**
 * The governance sidebar's own search: the same builders and the same matcher
 * as the global palette, narrowed to governance — its sections and everything
 * filed under them. Only governance sources are mounted.
 */
export function useGovernanceSearchGroups(
  /**
   * Until true the sources stay unmounted and nothing is fetched: the sidebar
   * is on every governance page, and a page must not pull the full findings
   * list just to show a search field.
   */
  enabled: boolean,
): ReadonlyArray<CommandPaletteGroup> {
  const { org } = useRegulationOrg();
  const organizationId = enabled ? (org?.id ?? null) : null;
  const orgSegment = organizationId ? orgUrlSegment(organizationId) : null;
  const governance = useGovernanceSources(organizationId);
  const go = useGlobalSearchNavigation(orgSegment);

  return useMemo(() => {
    const groups = buildGlobalSearchGroups(
      { orgSegment, ...governance, inbox: [], connections: [], automations: [] },
      go,
    );
    return groups.flatMap((group) => {
      if (GOVERNANCE_GROUP_VALUES.has(group.value)) return [group];
      if (group.value !== "global:places") return [];
      const items = group.items.filter((item) => item.value.startsWith("goto:governance:"));
      return items.length > 0 ? [{ ...group, label: "Sections", items }] : [];
    });
  }, [orgSegment, governance, go]);
}
